import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Spinner, Button, Form, Badge } from 'react-bootstrap';
import { FaNetworkWired, FaChevronDown, FaChevronRight } from 'react-icons/fa';

const AccountDiagramWidget = ({ accountId, region }) => {
  const [diagramData, setDiagramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDefaultVPCs, setShowDefaultVPCs] = useState(false);
  const [expandedVPCs, setExpandedVPCs] = useState({});

  useEffect(() => {
    fetchDiagramData();
  }, [accountId, region]);

  const fetchDiagramData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const tenantID = localStorage.getItem('tenantID');
      if (!tenantID) {
        throw new Error('Tenant ID not found');
      }

      const response = await fetch(
        `${process.env.REACT_APP_CLOUDMAP_API || 'https://9cdf5nf6ya.execute-api.us-west-2.amazonaws.com/prod'}/diagram?tenantId=${tenantID}&accountId=${accountId}&region=${region}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch diagram data: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data || result;
      
      console.log('Diagram API Response:', data);
      setDiagramData(data);
    } catch (err) {
      console.error('Error fetching diagram:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getVPCResourceCount = (vpcData) => {
    if (!vpcData.subnets) return 0;
    
    let count = 0;
    Object.values(vpcData.subnets).forEach(subnet => {
      if (subnet.resources && Array.isArray(subnet.resources)) {
        count += subnet.resources.length;
      }
    });
    return count;
  };

  const isDefaultVPC = (vpcData) => {
    const isDefaultCIDR = vpcData.cidr === '172.31.0.0/16';
    const resourceCount = getVPCResourceCount(vpcData);
    const subnetCount = vpcData.subnets ? Object.keys(vpcData.subnets).length : 0;
    
    return isDefaultCIDR && resourceCount === 0 && subnetCount <= 6;
  };

  const { visibleVPCs, hiddenCount, totalResourceCount } = useMemo(() => {
    if (!diagramData?.regions?.[region]?.vpcs) {
      return { visibleVPCs: {}, hiddenCount: 0, totalResourceCount: 0 };
    }

    const vpcs = diagramData.regions[region].vpcs;
    const filtered = {};
    let hidden = 0;
    let totalResources = 0;

    Object.entries(vpcs).forEach(([vpcId, vpcData]) => {
      const resourceCount = getVPCResourceCount(vpcData);
      totalResources += resourceCount;
      
      if (isDefaultVPC(vpcData) && !showDefaultVPCs) {
        hidden++;
      } else {
        filtered[vpcId] = vpcData;
      }
    });

    return { visibleVPCs: filtered, hiddenCount: hidden, totalResourceCount: totalResources };
  }, [diagramData, region, showDefaultVPCs]);

  const toggleVPC = (vpcId) => {
    setExpandedVPCs(prev => ({
      ...prev,
      [vpcId]: !prev[vpcId]
    }));
  };

  const getAwsIconPath = (resourceType) => {
    const iconMap = {
      'ec2': 'Compute/EC2.svg',
      'ec2-instances': 'Compute/EC2.svg',
      'rds': 'Database/RDS.svg',
      'rds-instances': 'Database/RDS.svg',
      'lambda': 'Compute/Lambda.svg',
      'lambda-functions': 'Compute/Lambda.svg',
      'dynamodb': 'Database/DynamoDB.svg',
      'dynamodb-tables': 'Database/DynamoDB.svg',
      's3': 'Storage/S3.svg',
      's3-buckets': 'Storage/S3.svg',
      'elb': 'Networking-Content-Delivery/Elastic-Load-Balancing.svg',
      'load-balancers': 'Networking-Content-Delivery/Elastic-Load-Balancing.svg',
      'alb': 'Networking-Content-Delivery/Elastic-Load-Balancing.svg'
    };
    
    const iconPath = iconMap[resourceType.toLowerCase()];
    return iconPath ? `/aws-icons/${iconPath}` : `/aws-icons/Compute/EC2.svg`;
  };

  if (loading) {
    return (
      <div className="text-center" style={{ padding: '60px 20px' }}>
        <Spinner animation="border" style={{ color: '#232f3e' }} />
        <p style={{ marginTop: '16px', color: '#687078' }}>Loading architecture diagram...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center" style={{ padding: '60px 20px' }}>
        <div style={{ fontSize: '2rem', color: '#d13212', marginBottom: '12px' }}>⚠</div>
        <p style={{ color: '#687078' }}>{error}</p>
        <Button
          size="sm"
          style={{ marginTop: '12px', background: '#232f3e', border: 'none' }}
          onClick={fetchDiagramData}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!diagramData?.regions?.[region]?.vpcs || Object.keys(diagramData.regions[region].vpcs).length === 0) {
    return (
      <div className="text-center" style={{ padding: '60px 20px' }}>
        <p style={{ color: '#687078' }}>No VPCs found in {region}</p>
      </div>
    );
  }

  const vpcCount = Object.keys(visibleVPCs).length;

  return (
    <div style={{ background: '#f7f9fa', minHeight: '400px', padding: '20px' }}>
      {/* Header Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0" style={{ color: '#232f3e', fontWeight: '600', fontSize: '16px' }}>
            Architecture Diagram - {region}
          </h6>
          <small style={{ color: '#687078', fontSize: '13px' }}>
            {vpcCount} VPC{vpcCount !== 1 ? 's' : ''}, {totalResourceCount} resource{totalResourceCount !== 1 ? 's' : ''}
          </small>
        </div>
        
        {hiddenCount > 0 && (
          <Form.Check 
            type="switch"
            id="show-default-vpcs"
            label={`Show ${hiddenCount} default VPC${hiddenCount !== 1 ? 's' : ''}`}
            checked={showDefaultVPCs}
            onChange={(e) => setShowDefaultVPCs(e.target.checked)}
            style={{ color: '#687078', fontSize: '13px' }}
          />
        )}
      </div>

      {/* VPCs */}
      {Object.keys(visibleVPCs).length === 0 ? (
        <div className="text-center" style={{ padding: '40px 20px', background: 'white', borderRadius: '4px', border: '1px solid #d5dbdb' }}>
          <p style={{ color: '#687078', margin: 0 }}>No VPCs to display</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {Object.entries(visibleVPCs).map(([vpcId, vpcData]) => {
            const isExpanded = expandedVPCs[vpcId] !== false;
            const isDefault = isDefaultVPC(vpcData);
            const resourceCount = getVPCResourceCount(vpcData);
            const subnetCount = vpcData.subnets ? Object.keys(vpcData.subnets).length : 0;
            
            return (
              <div
                key={vpcId}
                style={{
                  background: 'white',
                  border: '2px solid #232f3e',
                  borderRadius: '4px',
                  padding: '0'
                }}
              >
                {/* VPC Header */}
                <div
                  style={{
                    background: '#232f3e',
                    color: 'white',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleVPC(vpcId)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <FaNetworkWired style={{ fontSize: '18px' }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        VPC: {vpcData.name || vpcId}
                      </div>
                      <small style={{ opacity: 0.85, fontSize: '12px' }}>
                        {vpcData.cidr} | {subnetCount} subnet{subnetCount !== 1 ? 's' : ''} | {resourceCount} resource{resourceCount !== 1 ? 's' : ''}
                      </small>
                    </div>
                  </div>
                  <div style={{ color: 'white' }}>
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '16px' }}>
                    
                    {/* Gateways Row */}
                    {(vpcData.internetGateways?.length > 0 || vpcData.natGateways?.length > 0) && (
                      <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #d5dbdb' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#232f3e', marginBottom: '8px', textTransform: 'uppercase' }}>
                          Gateways
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {vpcData.internetGateways?.map((igw, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: '#e7f6ec',
                              border: '1px solid #4caf50',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <img 
                                src="/aws-icons/Networking-Content-Delivery/Cloud-WAN.svg"
                                alt="IGW"
                                style={{ width: '24px', height: '24px' }}
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <span style={{ color: '#232f3e', fontWeight: '500' }}>
                                IGW: {igw.name || igw.id}
                              </span>
                            </div>
                          ))}
                          {vpcData.natGateways?.map((nat, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: '#e3f2fd',
                              border: '1px solid #2196f3',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <img 
                                src="/aws-icons/Networking-Content-Delivery/NAT-Gateway.svg"
                                alt="NAT"
                                style={{ width: '24px', height: '24px' }}
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <span style={{ color: '#232f3e', fontWeight: '500' }}>
                                NAT: {nat.name || nat.id}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Subnets */}
                    {vpcData.subnets && Object.keys(vpcData.subnets).length > 0 ? (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#232f3e', marginBottom: '12px', textTransform: 'uppercase' }}>
                          Subnets & Resources
                        </div>
                        <Row className="g-3">
                          {Object.entries(vpcData.subnets).map(([subnetId, subnetData]) => {
                            const hasResources = subnetData.resources && subnetData.resources.length > 0;
                            const isPublic = subnetData.type === 'public';
                            
                            return (
                              <Col lg={6} key={subnetId}>
                                <div style={{
                                  border: `2px dashed ${isPublic ? '#4caf50' : '#2196f3'}`,
                                  borderRadius: '4px',
                                  padding: '12px',
                                  background: isPublic ? '#f1f8f4' : '#f0f7ff',
                                  minHeight: '120px'
                                }}>
                                  {/* Subnet Header */}
                                  <div style={{ marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                      <span style={{ fontWeight: '600', fontSize: '13px', color: '#232f3e' }}>
                                        {subnetData.name || subnetId}
                                      </span>
                                      <Badge 
                                        bg={isPublic ? 'success' : 'primary'} 
                                        style={{ fontSize: '10px', padding: '3px 8px' }}
                                      >
                                        {isPublic ? 'Public' : 'Private'}
                                      </Badge>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#687078' }}>
                                      {subnetData.cidr}
                                      {subnetData.az && ` | ${subnetData.az}`}
                                    </div>
                                  </div>
                                  
                                  {/* Resources */}
                                  {hasResources ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {subnetData.resources.map((resource, idx) => (
                                        <div
                                          key={idx}
                                          style={{
                                            background: 'white',
                                            border: '1px solid #d5dbdb',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                          }}
                                        >
                                          <img 
                                            src={getAwsIconPath(resource.type)}
                                            alt={resource.type}
                                            style={{ width: '32px', height: '32px', flexShrink: 0 }}
                                            onError={(e) => {
                                              e.target.src = '/aws-icons/default.svg';
                                              e.target.onerror = null;
                                            }}
                                          />
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                              color: '#232f3e',
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}>
                                              {resource.name || resource.id}
                                            </div>
                                            <div style={{ color: '#687078', fontSize: '11px' }}>
                                              {resource.type}
                                              {resource.instanceType && ` | ${resource.instanceType}`}
                                              {resource.state && ` | ${resource.state}`}
                                            </div>
                                            {resource.privateIp && (
                                              <div style={{ color: '#879596', fontSize: '10px', marginTop: '2px' }}>
                                                {resource.privateIp}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ 
                                      textAlign: 'center', 
                                      padding: '20px', 
                                      color: '#879596',
                                      fontSize: '12px',
                                      fontStyle: 'italic'
                                    }}>
                                      No resources
                                    </div>
                                  )}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#687078', fontSize: '13px' }}>
                        No subnets in this VPC
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountDiagramWidget;
